import React, { useState, useMemo, useEffect } from 'react';
import { ChevronUp, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

/**
 * SimpleTable - Reusable sortable/paginated table component
 *
 * Features:
 * - Click column headers to sort (ascending/descending, numeric-aware)
 * - Automatic pagination with configurable page size
 * - Custom cell renderers via columns[].render
 * - Row click handler
 * - Conditional row styling via rowClassName callback
 * - Compact mode for dashboard/card contexts
 * - Empty state message
 *
 * Usage:
 * ```jsx
 * const columns = [
 *   { key: 'name', label: 'Name' },
 *   { key: 'status', label: 'Status', render: (value, row) => <Badge>{value}</Badge> },
 *   { key: 'actions', label: '', sortable: false, render: (_, row) => <Button>Edit</Button> }
 * ];
 *
 * <SimpleTable
 *   columns={columns}
 *   data={myData}
 *   pageSize={15}
 *   emptyMessage="No data found"
 *   onRowClick={(row) => console.log(row)}
 *   rowClassName={(row) => row.expired ? 'bg-red-50' : ''}
 *   rowHoverClass="hover:bg-amber-50"
 *   compact={false}
 * />
 * ```
 */

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100, 250];

function SimpleTable({
  columns,
  data,
  pageSize: initialPageSize = 25,
  pageSizeSelector = true,
  emptyMessage,
  onRowClick,
  rowClassName,
  rowHoverClass = 'hover:bg-gray-50',
  compact = false
}) {
  const { t } = useTranslation();
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(initialPageSize);

  // Numeric-aware sort
  const sortedData = useMemo(() => {
    if (!sortKey) return data;
    return [...data].sort((a, b) => {
      const aVal = a[sortKey] ?? '';
      const bVal = b[sortKey] ?? '';
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
      }
      return sortDir === 'asc'
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });
  }, [data, sortKey, sortDir]);

  // Paginate
  const totalPages = Math.ceil(sortedData.length / pageSize);
  const pagedData = sortedData.slice(page * pageSize, (page + 1) * pageSize);

  // Reset page when data changes
  useEffect(() => {
    setPage(0);
  }, [data]);

  // Handle sort click (resets page)
  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
    setPage(0);
  };

  // Styling based on compact mode
  const tableClass = compact ? 'w-full text-sm' : 'w-full';
  const theadClass = compact ? '' : 'bg-gray-50 border-b';
  const theadTrClass = compact ? 'border-b border-gray-200' : '';
  const thBase = compact
    ? 'text-left py-2 px-2 text-gray-500 font-normal'
    : 'px-4 py-3 text-left text-sm font-semibold text-gray-700';
  const tdClass = compact ? 'py-2 px-2' : 'px-4 py-3 text-sm text-gray-700';
  const sortIconSize = compact ? 'w-3 h-3' : 'w-4 h-4';

  // Empty state
  if (data.length === 0) {
    return (
      <div className={compact ? 'text-gray-500 text-center py-4' : 'p-8 text-center text-gray-500'}>
        {emptyMessage ?? t('common.states.empty')}
      </div>
    );
  }

  return (
    <div>
      <table className={tableClass}>
        <thead className={theadClass}>
          <tr className={theadTrClass}>
            {columns.map(col => (
              <th
                key={col.key}
                onClick={() => col.sortable !== false && handleSort(col.key)}
                className={`${thBase} ${
                  col.sortable !== false ? 'cursor-pointer hover:bg-gray-100' : ''
                }`}
              >
                <div className="flex items-center gap-1">
                  {col.label}
                  {sortKey === col.key && (
                    sortDir === 'asc'
                      ? <ChevronUp className={sortIconSize} />
                      : <ChevronDown className={sortIconSize} />
                  )}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className={compact ? '' : 'divide-y'}>
          {pagedData.map((row, idx) => {
            const customClass = typeof rowClassName === 'function' ? rowClassName(row) : '';
            return (
              <tr
                key={row.id || idx}
                className={`${compact ? 'border-b border-gray-100' : ''} ${rowHoverClass} ${onRowClick ? 'cursor-pointer' : ''} ${customClass}`}
                onClick={() => onRowClick && onRowClick(row)}
              >
                {columns.map(col => (
                  <td key={col.key} className={tdClass}>
                    {col.render ? col.render(row[col.key], row) : row[col.key]}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Pagination */}
      {(totalPages > 1 || (pageSizeSelector && !compact && sortedData.length > PAGE_SIZE_OPTIONS[0])) && (
        compact ? (
          <div className="flex items-center justify-between mt-3 text-sm text-gray-500">
            <span>{sortedData.length} éléments</span>
            <div className="flex gap-1">
              <button
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
                className="px-2 py-1 border rounded disabled:opacity-50"
              >
                ←
              </button>
              <span className="px-2 py-1">{page + 1} / {totalPages}</span>
              <button
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="px-2 py-1 border rounded disabled:opacity-50"
              >
                →
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50">
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-600">
                {t('common.pagination.range', { start: page * pageSize + 1, end: Math.min((page + 1) * pageSize, sortedData.length), total: sortedData.length })}
              </span>
              {pageSizeSelector && sortedData.length > PAGE_SIZE_OPTIONS[0] && (
                <select
                  value={pageSize}
                  onChange={(e) => { setPageSize(Number(e.target.value)); setPage(0); }}
                  className="text-sm border rounded px-2 py-1 bg-white text-gray-600"
                >
                  {PAGE_SIZE_OPTIONS.filter(s => s <= sortedData.length || s === PAGE_SIZE_OPTIONS[0]).map(s => (
                    <option key={s} value={s}>{t('common.pagination.perPageOption', { count: s })}</option>
                  ))}
                  <option value={sortedData.length}>{t('common.pagination.all', { count: sortedData.length })}</option>
                </select>
              )}
            </div>
            {totalPages > 1 && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(0)}
                  disabled={page === 0}
                  className="px-2 py-1 text-xs rounded hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
                  title={t('common.pagination.first')}
                >
                  1
                </button>
                <button
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="p-1 rounded hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <span className="px-2 py-1 text-sm">
                  {page + 1} / {totalPages}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                  className="p-1 rounded hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setPage(totalPages - 1)}
                  disabled={page >= totalPages - 1}
                  className="px-2 py-1 text-xs rounded hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
                  title={t('common.pagination.last')}
                >
                  {totalPages}
                </button>
              </div>
            )}
          </div>
        )
      )}
    </div>
  );
}

export default SimpleTable;
